import React, { useState, useLayoutEffect, ReactNode, FC } from "react";
import { MatterEditorContent, Icons } from "../../components";
import { Button, Radio, Layout, Breadcrumb } from "antd";
import type { Tabs } from "antd";
import {
  Matter,
  NewClientValue,
  PricingType,
  MatterExternalMatterUpdate,
  MatterStatusUpdate,
  MatterNonExternalFieldsUpdate,
  Settings,
  ClientFilterData,
  MatterType,
  MatterTemplate,
  MatterFormMode,
} from "../../data-types";
import { TOP_DOWN_PRICING_TAB, HISTORICAL_MATTERS_TAB } from "~/hooks/use-active-tab";

import styles from "./matter-editor-view.css";
import { Link } from "react-router-dom";
import { PartitionOutlined } from "@ant-design/icons";

const {
  layout: layoutClassName,
  content: contentClassName,
  ["pricing-summary-close-button"]: pricingSummaryCloseButtonClassName,
  ["drawer-controls"]: drawerControlClassname,
} = styles as any;

enum Drawers {
  pricing = "pricing",
  history = "history",
  locked = "locked",
}

export type MatterEditorViewProps<T extends MatterType, U extends MatterFormMode> = {
  editorUnlocked: boolean;
  matter: T extends "template" ? MatterTemplate : Matter;
  onClientSave: (newClient: NewClientValue) => void;
  onMatterStatusUpdate: (matter: MatterStatusUpdate) => Promise<void>;
  onMatterExternalMatterUpdate: (matter: MatterExternalMatterUpdate) => Promise<void>;
  onMatterUpdate: (matter: MatterNonExternalFieldsUpdate<T, U>) => Promise<void>;
  clientFilterData?: ClientFilterData;
  settings: Settings;
  tabs: Array<typeof Tabs.TabPane>;
  activeTab: string;
  onChangeTab: (newActiveTab: string) => void;
  sidebarContents: Record<string, ReactNode>;
  isTemplate?: boolean;
  parentMatter?: {
    id: number;
    title: string;
    matters: {
      id: number;
      title: string;
    }[];
  } | null;
};

const DRAWER_WIDTH = "350px";
export const MatterEditorView = <T extends MatterType, U extends MatterFormMode>({
  matter,
  tabs,
  activeTab,
  onChangeTab,
  sidebarContents,
  editorUnlocked,
  isTemplate,
  parentMatter,
}: MatterEditorViewProps<T, U>): ReturnType<FC<MatterEditorViewProps<T, U>>> => {
  const { pricingType } = matter;
  const [openDrawer, setOpenDrawer] = useState<keyof typeof sidebarContents | null>(Drawers.locked);
  const [userWantsDrawerOpen, setUserWantsDrawerOpen] = useState(true);

  const hasPricing = !!pricingType && pricingType !== PricingType.None;
  useLayoutEffect(() => {
    if (hasPricing) {
      setOpenDrawer(
        activeTab === TOP_DOWN_PRICING_TAB || activeTab === HISTORICAL_MATTERS_TAB
          ? null
          : Drawers.pricing
      );
    }
  }, [hasPricing]);

  const handleTabChange = (key: string) => {
    if (key === TOP_DOWN_PRICING_TAB || key === HISTORICAL_MATTERS_TAB) {
      setOpenDrawer(null);
    } else {
      setOpenDrawer(userWantsDrawerOpen ? Drawers.pricing : null);
    }
    onChangeTab(key);
  };

  return (
    <Layout className={layoutClassName}>
      <Layout.Content className={contentClassName}>
        {parentMatter && (
          <Breadcrumb style={{ padding: 2, marginLeft: 5, fontSize: "1.5rem" }}>
            <Breadcrumb.Item>
              <Link to={`/parent/${parentMatter.id}`}>
                <PartitionOutlined /> Multi Practice Area Workspace
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item
              menu={{
                items: parentMatter.matters
                  .filter((m) => m.id !== matter.id)
                  .map((x) => ({
                    key: x.id.toString(),
                    label: (
                      <span>
                        <Link to={`/matter-editor/${x.id}`}>
                          <span
                            style={{
                              padding: "0 5px",
                              width: "100%",
                              fontSize: "1.5rem",
                            }}
                          >
                            {x.title}
                          </span>
                        </Link>
                      </span>
                    ),
                  })),
              }}
            >
              {matter.title}
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        <MatterEditorContent
          matterId={matter.id}
          matterTitle={matter.title}
          template={isTemplate ?? false}
          activeTab={activeTab}
          onChangeTab={handleTabChange}
        >
          {tabs}
        </MatterEditorContent>
      </Layout.Content>
      {!isTemplate && (
        <Layout.Sider theme="light" width={350} collapsedWidth={0} collapsed={!openDrawer}>
          {openDrawer && sidebarContents[openDrawer]}
          {openDrawer && (
            <Button
              className={pricingSummaryCloseButtonClassName}
              icon={Icons.Close}
              onClick={() => {
                setUserWantsDrawerOpen(false);
                setOpenDrawer(null);
              }}
            />
          )}
          {editorUnlocked && (
            <Radio.Group
              className={drawerControlClassname}
              value={openDrawer}
              onChange={(e) => {
                setUserWantsDrawerOpen(true);
                setOpenDrawer(e.target.value);
              }}
              style={openDrawer === null ? {} : { right: DRAWER_WIDTH }}
            >
              {Object.keys(sidebarContents)
                .filter((k) => k !== "locked")
                .map((k) => (
                  <Radio.Button key={k} value={k}>
                    {k[0].toUpperCase() + k.slice(1)}
                  </Radio.Button>
                ))}
            </Radio.Group>
          )}
        </Layout.Sider>
      )}
    </Layout>
  );
};
